import { EyeOpenIcon } from '@radix-ui/react-icons'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, { type Dispatch, type SetStateAction, useMemo, useState } from 'react'

import CohortSelect from 'components/admin/CohortSelect'
import EmployerOpenEnrollmentDetailsSheet from 'components/admin/EmployerOpenEnrollmentDetailsSheet'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { adminEmployers } from 'rails_routes'
import { Badge, Button } from 'slab'
import { useGetAdminEmployerOpenEnrollment } from 'utils/web_api'
import { type AdminEmployerOpenEnrollment, type GetAdminEmployerOpenEnrollmentParams } from 'utils/web_api.schemas'

export type EmployerOpenEnrollmentFilters = {
  status?: string[]
  cohort?: string[]
  employer_ids?: string[]
  min_employee_count?: string
  max_employee_count?: string
  min_final_rates?: string
  max_final_rates?: string
}

type GetEmployerOpenEnrollmentQueryArgs = {
  openEnrollmentYear: number
  filters: EmployerOpenEnrollmentFilters
  pagination: PaginationState
  sorting: SortingState
}

type ExtendedApiParams = GetAdminEmployerOpenEnrollmentParams & Record<string, unknown>

const convertToApiParams = ({ openEnrollmentYear, filters, pagination, sorting }: GetEmployerOpenEnrollmentQueryArgs): ExtendedApiParams => {
  const params: ExtendedApiParams = {
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    open_enrollment_year: openEnrollmentYear,
  }

  // Add filter parameters
  if (filters.status && filters.status.length > 0) {
    params['filter[status][]'] = filters.status
  }

  if (filters.cohort && filters.cohort.length > 0) {
    params['filter[cohort][]'] = filters.cohort
  }

  if (filters.employer_ids && filters.employer_ids.length > 0) {
    params['filter[employer_ids][]'] = filters.employer_ids
  }

  if (filters.min_employee_count) {
    params['filter[min_employee_count]'] = filters.min_employee_count
  }

  if (filters.max_employee_count) {
    params['filter[max_employee_count]'] = filters.max_employee_count
  }

  if (filters.min_final_rates) {
    params['filter[min_final_rates]'] = filters.min_final_rates
  }

  if (filters.max_final_rates) {
    params['filter[max_final_rates]'] = filters.max_final_rates
  }

  // Add sorting parameters
  if (sorting.length > 0) {
    params[`sort[${sorting[0].id}]`] = sorting[0].desc ? 'desc' : 'asc'
  }

  return params
}

interface EmployerOpenEnrollmentTableProps {
  openEnrollmentYear: number
  filters: EmployerOpenEnrollmentFilters
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
}

const columnHelper = createColumnHelper<AdminEmployerOpenEnrollment>()

const StatusBadge = ({ status }: { status: string }) => {
  const displayText = status.split('_').map((word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')

  switch (status) {
    case 'not_ready':
      return <Badge type='neutral'>{displayText}</Badge>
    case 'ready_for_open_enrollment':
      return <Badge type='info'>{displayText}</Badge>
    case 'employees_confirmed':
      return <Badge type='caution'>{displayText}</Badge>
    case 'accepted_preliminary_quote':
      return <Badge type='warning'>{displayText}</Badge>
    case 'accepted_final_quote':
      return <Badge type='success'>{displayText}</Badge>
    default:
      return <Badge type='neutral'>{displayText}</Badge>
  }
}

const EmployerOpenEnrollmentTable: React.FC<EmployerOpenEnrollmentTableProps> = ({
  openEnrollmentYear,
  filters,
  pagination,
  setPagination,
  sorting,
  setSorting,
}) => {
  const [selectedEmployerConfigId, setSelectedEmployerConfigId] = useState<string | null>(null)
  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  const { data, isLoading, isError } = useGetAdminEmployerOpenEnrollment(
    convertToApiParams({ openEnrollmentYear, filters, pagination, sorting })
  )

  // Update pagination metadata when data changes
  React.useEffect(() => {
    if (data?.pagination) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      id: 'employer_name',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='Employer' />
      ),
      cell: ({ row }) => {
        const employer = row.original
        const profile = employer.profile
        const name = profile?.name || 'Unknown Name'
        return (
          <a
            href={adminEmployers.show({ id: employer.id })}
            className='text-blue-600 hover:text-blue-800 underline font-medium'
          >
            {name}
          </a>
        )
      },
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'cohort',
      header: 'Cohort',
      cell: ({ row }) => {
        return <CohortSelect employer={row.original} />
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.open_enrollment_config?.eligible_employee_count, {
      id: 'eligible_employee_count',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='Eligible Employees' />
      ),
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        return config?.eligible_employee_count ?? '—'
      },
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        if (!config) return '—'

        const status = config.status || 'not_ready'
        return <StatusBadge status={status} />
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.open_enrollment_config?.current_year_monthly_employer_contribution?.formatted_amount, {
      id: 'current_year_monthly_employer_contribution',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='Current Monthly Contribution' />
      ),
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        const amount = config?.current_year_monthly_employer_contribution?.formatted_amount
        return amount ?? '—'
      },
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.open_enrollment_config?.open_enrollment_year_monthly_employer_contribution?.formatted_amount, {
      id: 'open_enrollment_year_monthly_employer_contribution',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='New Monthly Contribution' />
      ),
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        const amount = config?.open_enrollment_year_monthly_employer_contribution?.formatted_amount
        return amount ?? '—'
      },
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.open_enrollment_config?.monthly_employer_contribution_delta?.formatted_amount, {
      id: 'monthly_employer_contribution_delta',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='Monthly Contribution Delta' />
      ),
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        const amount = config?.monthly_employer_contribution_delta?.formatted_amount
        return amount ?? '—'
      },
      enableSorting: true,
    }),
    columnHelper.accessor((row) => row.open_enrollment_config?.percentage_of_employees_with_final_rates, {
      id: 'percentage_of_employees_with_final_rates',
      header: (info) => (
        <SortableHeader<AdminEmployerOpenEnrollment> {...info} headerText='% Employees with Final Rates' />
      ),
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        return config?.percentage_of_employees_with_final_rates !== undefined
          ? `${config.percentage_of_employees_with_final_rates}%`
          : '—'
      },
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'details',
      header: 'Details',
      cell: ({ row }) => {
        const config = row.original.open_enrollment_config
        if (!config?.id) return '—'

        return (
          <Button
            size='small'
            variant='secondary'
            onClick={() => setSelectedEmployerConfigId(config.id)}
          >
            <EyeOpenIcon />
          </Button>
        )
      },
    }),
  ], [])

  const table = useReactTable({
    data: data?.employers ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  return (
    <>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='No employers found for open enrollment.'
        setSorting={setSorting}
        defaultSortingState={[]}
        paginationLabelSingular='employer'
        paginationMetadata={paginationMetadata}
        tableMessageHeightClass='h-32'
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1,
          })}
      />
      <EmployerOpenEnrollmentDetailsSheet
        configId={selectedEmployerConfigId}
        onClose={() => setSelectedEmployerConfigId(null)}
      />
    </>
  )
}

export default EmployerOpenEnrollmentTable
